import type { Provider } from "../types";

export const providers: Provider[] = [
  {
    id: "openai",
    iconSlug: "openai",
    name: "OpenAI",
    website: "https://openai.com",
    docsUrl: "https://platform.openai.com/docs",
    pricingUrl: "https://developers.openai.com/api/docs/pricing",
    description:
      "GPT Image and Sora video APIs. Token-based image pricing; per-second video pricing.",
    regions: ["global", "US data residency"],
  },
  {
    id: "google-gemini",
    iconSlug: "googlegemini",
    name: "Google Gemini API",
    website: "https://ai.google.dev",
    docsUrl: "https://ai.google.dev/gemini-api/docs",
    pricingUrl: "https://ai.google.dev/gemini-api/docs/pricing",
    description:
      "Gemini native image models, Imagen (deprecated timeline), and Veo video generation.",
    notes: "Imagen 4 endpoints scheduled shutdown Aug 17, 2026 — migrate to Gemini Flash Image.",
  },
  {
    id: "google-vertex",
    iconSlug: "googlecloud",
    name: "Google Vertex AI",
    website: "https://cloud.google.com/vertex-ai",
    docsUrl: "https://cloud.google.com/vertex-ai/generative-ai/docs",
    pricingUrl: "https://cloud.google.com/vertex-ai/generative-ai/pricing",
    description: "Enterprise Google Cloud hosting for Imagen, Veo, and Gemini models.",
  },
  {
    id: "fal",
    name: "fal.ai",
    website: "https://fal.ai",
    docsUrl: "https://fal.ai/docs",
    pricingUrl: "https://fal.ai/pricing",
    description:
      "Serverless generative media inference platform hosting Flux, Kling, Wan, Luma, Veo, and many more.",
  },
  {
    id: "replicate",
    iconSlug: "replicate",
    name: "Replicate",
    website: "https://replicate.com",
    docsUrl: "https://replicate.com/docs",
    pricingUrl: "https://replicate.com/pricing",
    description: "Run open and commercial models with per-second / per-output pricing.",
  },
  {
    id: "runway",
    name: "Runway",
    website: "https://runwayml.com",
    docsUrl: "https://docs.dev.runwayml.com",
    pricingUrl: "https://docs.dev.runwayml.com/guides/pricing/",
    description: "Gen-3 / Gen-4 video generation API with credit-based billing ($0.01/credit).",
  },
  {
    id: "luma",
    name: "Luma AI",
    website: "https://lumalabs.ai",
    docsUrl: "https://docs.lumalabs.ai",
    pricingUrl: "https://lumalabs.ai/dream-machine",
    description: "Dream Machine / Ray video models; also available via fal.ai hosts.",
  },
  {
    id: "stability",
    iconSlug: "stabilityai",
    name: "Stability AI",
    website: "https://stability.ai",
    docsUrl: "https://platform.stability.ai/docs",
    pricingUrl: "https://platform.stability.ai/pricing",
    description: "Stable Image / Stable Diffusion API. Credits: 1 credit = $0.01.",
  },
  {
    id: "bfl",
    name: "Black Forest Labs",
    website: "https://bfl.ai",
    docsUrl: "https://docs.bfl.ai",
    pricingUrl: "https://bfl.ai/pricing",
    description: "Official FLUX.2 family API (Pro, Flex, Max, Klein). Per-MP and per-image rates.",
  },
  {
    id: "ideogram",
    name: "Ideogram",
    website: "https://ideogram.ai",
    docsUrl: "https://developer.ideogram.ai",
    pricingUrl: "https://ideogram.ai/api-pricing/",
    description: "Strong text rendering in images. Per-image Turbo / Default / Quality tiers.",
  },
  {
    id: "leonardo",
    name: "Leonardo.AI",
    website: "https://leonardo.ai",
    docsUrl: "https://docs.leonardo.ai",
    pricingUrl: "https://leonardo.ai/api",
    description: "Creative image models (Phoenix, Lucid Origin). Also on Cloudflare Workers AI.",
  },
  {
    id: "xai",
    iconSlug: "x",
    name: "xAI",
    website: "https://x.ai",
    docsUrl: "https://docs.x.ai",
    pricingUrl: "https://docs.x.ai/developers/pricing",
    description: "Grok Imagine image and video APIs with flat per-image / per-second pricing.",
  },
  {
    id: "cloudflare",
    iconSlug: "cloudflare",
    name: "Cloudflare Workers AI",
    website: "https://developers.cloudflare.com/workers-ai/",
    docsUrl: "https://developers.cloudflare.com/workers-ai/",
    pricingUrl: "https://developers.cloudflare.com/workers-ai/platform/pricing/",
    description: "Edge-hosted Flux and Leonardo image models billed in Neurons ($0.011 / 1k Neurons).",
  },
  {
    id: "bedrock",
    iconSlug: "amazonwebservices",
    name: "Amazon Bedrock",
    website: "https://aws.amazon.com/bedrock/",
    docsUrl: "https://docs.aws.amazon.com/bedrock/",
    pricingUrl: "https://aws.amazon.com/bedrock/pricing/",
    description: "Managed foundation models including Stability image services and Titan Image.",
  },
  {
    id: "azure",
    iconSlug: "microsoftazure",
    name: "Azure OpenAI / AI Foundry",
    website: "https://azure.microsoft.com/products/ai-services/openai-service",
    docsUrl: "https://learn.microsoft.com/azure/ai-services/openai/",
    pricingUrl: "https://azure.microsoft.com/pricing/details/cognitive-services/openai-service/",
    description: "Enterprise hosting of OpenAI image models with regional compliance.",
  },
  {
    id: "together",
    name: "Together AI",
    website: "https://together.ai",
    docsUrl: "https://docs.together.ai",
    pricingUrl: "https://www.together.ai/pricing",
    description: "Open-weight model hosting including Flux and other image models.",
  },
  {
    id: "fireworks",
    name: "Fireworks AI",
    website: "https://fireworks.ai",
    docsUrl: "https://docs.fireworks.ai",
    pricingUrl: "https://fireworks.ai/pricing",
    description: "Fast inference platform with FLUX and open image models.",
  },
  {
    id: "kie",
    name: "Kie.ai (Kling hosts)",
    website: "https://kie.ai",
    docsUrl: "https://kie.ai/docs",
    pricingUrl: "https://kie.ai/pricing",
    description: "Third-party host for Kling and related Chinese video models.",
    notes: "Pricing often opaque / credit-based; verify on vendor dashboard.",
  },
  {
    id: "atlascloud",
    name: "AtlasCloud",
    website: "https://www.atlascloud.ai",
    docsUrl: "https://www.atlascloud.ai/docs",
    pricingUrl: "https://www.atlascloud.ai/pricing",
    description: "Multi-model generative media API aggregator.",
  },
  {
    id: "groq",
    iconSlug: "groq",
    name: "Groq",
    website: "https://groq.com",
    docsUrl: "https://console.groq.com/docs",
    pricingUrl: "https://groq.com/pricing",
    description: "Primarily LLM inference; limited generative media relevance today.",
    notes: "Included for completeness — no first-party image/video gen models as of lastVerified.",
  },
  {
    id: "byteplus",
    name: "BytePlus / Seedance hosts",
    website: "https://www.byteplus.com",
    docsUrl: "https://docs.byteplus.com",
    pricingUrl: "https://www.byteplus.com/en/pricing",
    description: "Hosts for Seedance and related ByteDance generative video models.",
  },
  {
    id: "alibaba",
    iconSlug: "alibabacloud",
    name: "Alibaba Cloud (Wan hosts)",
    website: "https://www.alibabacloud.com",
    docsUrl: "https://www.alibabacloud.com/help/en/model-studio/",
    pricingUrl: "https://www.alibabacloud.com/product/modelstudio",
    description: "Official and regional hosts for Wan (通义万相) video/image models.",
  },
  {
    id: "recraft",
    name: "Recraft",
    website: "https://www.recraft.ai",
    docsUrl: "https://www.recraft.ai/docs",
    pricingUrl: "https://www.recraft.ai/pricing",
    description: "Design-oriented image generation (Recraft V3) with strong vector/SVG support.",
  },
  {
    id: "minimax",
    name: "MiniMax",
    website: "https://www.minimax.io",
    docsUrl: "https://www.minimax.io/platform",
    pricingUrl: "https://www.minimax.io/pricing",
    description: "Hailuo video generation models; also available via fal.ai.",
  },

  {
    id: "deepinfra",
    iconSlug: "deepinfra",
    name: "DeepInfra",
    website: "https://deepinfra.com",
    docsUrl: "https://deepinfra.com/docs",
    pricingUrl: "https://deepinfra.com/pricing",
    description: "Hosted open models including FLUX and SDXL with per-output pricing.",
  },
  {
    id: "novita",
    name: "Novita AI",
    website: "https://novita.ai",
    docsUrl: "https://novita.ai/docs",
    pricingUrl: "https://novita.ai/pricing",
    description: "Generative media API host for Flux, SD, Wan, Kling and more.",
  },
  {
    id: "pixverse",
    name: "PixVerse",
    website: "https://pixverse.ai",
    docsUrl: "https://docs.platform.pixverse.ai",
    pricingUrl: "https://pixverse.ai/pricing",
    description: "PixVerse video generation API (V4/V5). Credit and host-listed USD rates.",
  },
  {
    id: "pika",
    name: "Pika",
    website: "https://pika.art",
    docsUrl: "https://docs.pika.art",
    pricingUrl: "https://pika.art/pricing",
    description: "Pika video generation. Direct plans are credit-based; third-party hosts list USD.",
    notes: "Prefer fal/Replicate host listings for transparent API unit prices.",
  },
  {
    id: "getimg",
    name: "Getimg.ai",
    website: "https://getimg.ai",
    docsUrl: "https://docs.getimg.ai",
    pricingUrl: "https://getimg.ai/pricing",
    description: "Image generation API (Essential / Family / Realistic Vision and Flux hosts).",
  },
  {
    id: "freepik",
    iconSlug: "freepik",
    name: "Freepik AI",
    website: "https://www.freepik.com",
    docsUrl: "https://docs.freepik.com",
    pricingUrl: "https://www.freepik.com/ai/pricing",
    description: "Freepik generative image/video APIs for creative stock workflows.",
  },
  {
    id: "kling",
    name: "Kling AI (official)",
    website: "https://klingai.com",
    docsUrl: "https://app.klingai.com/global/dev/document-api",
    pricingUrl: "https://klingai.com/pricing",
    description: "Official Kling video API. Regional credit packs; fal hosts often clearer USD.",
  },
  {
    id: "wavespeed",
    name: "WaveSpeedAI",
    website: "https://wavespeed.ai",
    docsUrl: "https://wavespeed.ai/docs",
    pricingUrl: "https://wavespeed.ai/pricing",
    description: "Fast generative media inference API with Flux and video model hosts.",
  },

  {
    id: "krea",
    name: "Krea",
    website: "https://www.krea.ai",
    docsUrl: "https://www.krea.ai/app/api",
    pricingUrl: "https://www.krea.ai/app/api/pricing",
    description:
      "Creative AI platform with first-party Krea 2 (K2) image models plus an API aggregator for partner image/video models (Seedance, Kling, Veo, Runway, …).",
    notes: "API billing is separate from Krea consumer Pro/Max plans. Aggregator + own models.",
  },
  {
    id: "pruna",
    name: "Pruna.ai",
    website: "https://www.pruna.ai",
    docsUrl: "https://docs.pruna.ai/",
    pricingUrl: "https://www.pruna.ai/all-models",
    description:
      "Performance models (P-Image, P-Video family) optimized for speed/cost/quality. Also ships optimized endpoints via partner hosts.",
    partnerHosts: [
      "cloudflare",
      "replicate",
      "runware",
      "together",
      "wavespeed",
      "leonardo",
      "magnific",
      "runpod",
      "prodia",
      "segmind",
    ],
    notes:
      "Partner inference platforms listed as metadata — unique Pruna performance models are first-class catalog rows.",
  },

];
